/**
 * Heuro Cinema - Timeline Controller
 * Orchestrates the card animation sequence with pause/resume support.
 */

import type { Pillar } from '../types/content';
import { createCard, showCard, splitAndDock } from './cards';

let isPaused = false;
let translations: Record<string, string> = {};

export function setTimelineLanguage(_lang: string): void {
  // Language is stored for potential future use
}

export function setTimelineTranslations(t: Record<string, string>): void {
  translations = t;
}

export function togglePlayback(): void {
  isPaused = !isPaused;
  const hint = document.getElementById('playbackHint');
  if (hint) {
    hint.textContent = isPaused
      ? (translations.playResume || 'Click anywhere to continue')
      : (translations.playPause || 'Click anywhere to pause');
  }
}

export function isPausedState(): boolean {
  return isPaused;
}

export function pauseTimeline(): void {
  if (!isPaused) {
    isPaused = true;
    const hint = document.getElementById('playbackHint');
    if (hint) {
      hint.textContent = translations.playResume || 'Click anywhere to continue';
    }
  }
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Get timing from CSS variables (set by API config)
function getTimingMs(varName: string, defaultMs: number): number {
  const style = getComputedStyle(document.documentElement);
  const value = style.getPropertyValue(varName).trim();
  if (!value) return defaultMs;
  // Parse "800ms" or "0.8s"
  const match = value.match(/^([\d.]+)(ms|s)?$/);
  if (!match) return defaultMs;
  const num = parseFloat(match[1]);
  const unit = match[2] || 'ms';
  return unit === 's' ? num * 1000 : num;
}

async function waitWhilePaused(): Promise<void> {
  while (isPaused) {
    await wait(100);
  }
}

async function runPillarSequence(pillar: Pillar): Promise<void> {
  const flipDelay = getTimingMs('--card-flip-delay', 1000);
  const dockDuration = getTimingMs('--card-dock-duration', 600);

  // Create main card
  const mainCard = createCard(
    pillar.main,
    pillar.id,
    pillar.accent,
    true,
    0,
    pillar.dockPositions[0]
  );

  // Create split cards
  const splitCards = pillar.splits.map((split, i) =>
    createCard(
      split,
      pillar.id,
      pillar.accent,
      false,
      i + 1,
      pillar.dockPositions[i + 1]
    )
  );

  // Show and animate
  await showCard(mainCard);
  await waitWhilePaused();
  // Wait based on flip delay (how long card is shown before next action)
  await wait(flipDelay * 1.2);
  await waitWhilePaused();

  await splitAndDock(mainCard, splitCards);
  await waitWhilePaused();
  // Brief pause after docking before next pillar
  await wait(dockDuration * 0.6);
}

function isMobile(): boolean {
  return window.innerWidth < 768;
}

function switchToMobileDockedLayout(): void {
  document.body.classList.add('mobile-docked');

  // Insert pillar headers before each group of cards
  const container = document.getElementById('cardsContainer');
  if (!container) return;

  const cards = Array.from(container.querySelectorAll('.card.docked')) as HTMLElement[];
  let currentPillar = '';

  for (const card of cards) {
    const pillar = card.dataset.pillar || '';
    if (pillar !== currentPillar) {
      currentPillar = pillar;
      const header = document.createElement('div');
      header.className = 'mobile-pillar-header';
      header.textContent = pillar.toUpperCase();
      container.insertBefore(header, card);
    }
  }
}

export async function runFullTimeline(pillars: Pillar[]): Promise<void> {
  const dockDuration = getTimingMs('--card-dock-duration', 600);
  const mobile = isMobile();

  const centerOverlay = document.getElementById('centerOverlay');
  const playbackHint = document.getElementById('playbackHint');
  const ctaStage = document.getElementById('ctaStage');

  await wait(mobile ? 1200 : 2000);
  await waitWhilePaused();

  // Hide center overlay
  centerOverlay?.classList.remove('visible');
  await wait(400);

  // Run each pillar sequence
  for (let i = 0; i < pillars.length; i++) {
    await runPillarSequence(pillars[i]);
    if (i < pillars.length - 1) {
      await wait(dockDuration);
      await waitWhilePaused();
    }
  }

  await wait(dockDuration * 1.2);

  // Show CTA
  playbackHint?.classList.remove('visible');
  ctaStage?.classList.add('visible');

  // On mobile, switch to scrollable stacked layout
  if (mobile) {
    switchToMobileDockedLayout();
  }
}